import type { MarkdownToJSX } from 'markdown-to-jsx';
import Markdown from 'markdown-to-jsx';
import type { FC } from 'react';
import { Fragment, useRef } from 'react';

import { ExpandableSources } from '../ExpandableSources/ExpandableSources';
import { MessageActions } from '../MessageActions/MessageActions';
import type { AgentChatMessage } from '../types';
import { agentOutputContentCss, agentOutputCss } from './AgentMessage.styles';

type DisallowedTag = {
  tag: string;
  replacement: typeof Fragment | string | null;
};

/**
 * Tags to override when rendering markdown. Each tag maps to a replacement:
 *
 * - `null` — remove entirely
 * - `Fragment` — unwrap, rendering only children
 * - `'p'`, `'span'`, etc. — wrap children in that element
 */
const disallowedTags: DisallowedTag[] = [
  { tag: 'h1', replacement: 'p' },
  { tag: 'h2', replacement: 'p' },
  { tag: 'h3', replacement: 'p' },
  { tag: 'h4', replacement: 'p' },
  { tag: 'h5', replacement: 'p' },
  { tag: 'h6', replacement: 'p' },
  { tag: 'blockquote', replacement: 'p' },
  { tag: 'pre', replacement: 'p' },
  { tag: 'code', replacement: 'span' },
  { tag: 'del', replacement: 'span' },
  { tag: 'table', replacement: Fragment },
  { tag: 'thead', replacement: Fragment },
  { tag: 'tbody', replacement: Fragment },
  { tag: 'tr', replacement: Fragment },
  { tag: 'td', replacement: 'p' },
  { tag: 'th', replacement: 'p' },
  { tag: 'img', replacement: null },
  { tag: 'hr', replacement: null },
  { tag: 'input', replacement: null },
];

const markdownOptions: MarkdownToJSX.Options = {
  forceBlock: true,
  wrapper: Fragment,
  disableParsingRawHTML: true,
  overrides: {
    ...Object.fromEntries(
      disallowedTags.map(({ tag, replacement }) => [
        tag,
        ({ children }: any) => {
          if (replacement === null) return null;
          const Tag = replacement;
          return children ? <Tag>{children}</Tag> : null;
        },
      ])
    ),
    a: {
      component: 'a',
      props: {
        target: '_blank',
        rel: 'noopener',
      },
    },
  },
};

interface AgentMessageProps {
  message: AgentChatMessage;
  isLatest?: boolean;
  onCopy?: (text: string, html?: string) => void;
  onRedo?: () => void;
  onToggleSources?: () => void;
}

/** Renders an AI agent response message aligned to the left of the conversation. */
export const AgentMessage: FC<AgentMessageProps> = ({
  message,
  isLatest,
  onCopy,
  onRedo,
  onToggleSources,
}) => {
  const { id, content, sources } = message ?? {};

  const contentRef = useRef<HTMLDivElement>(null);

  return (
    <div className={agentOutputCss}>
      <div className={agentOutputContentCss} ref={contentRef}>
        <Markdown options={markdownOptions}>{content}</Markdown>
      </div>
      {!!sources?.length && <ExpandableSources sources={sources} onToggle={onToggleSources} />}
      <MessageActions
        id={id}
        allowRedo={isLatest}
        onCopy={() =>
          onCopy?.(contentRef.current?.innerText ?? '', contentRef.current?.innerHTML ?? '')
        }
        onRedo={onRedo}
      />
    </div>
  );
};

AgentMessage.displayName = 'AgentMessage';
